import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { translateManyClient } from "../../../utils/ItClient";
import { getToken } from "../../../utils/localStorageMethods";
import i18n from "../i18n";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/driver`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

export const driversSlice = createApi({
  reducerPath: "driversSlice",
  baseQuery: baseQuery,
  tagTypes: ["drivers", "schedules"],
  endpoints: (builder) => ({
    // GET Drivers List
    getDrivers: builder.query({
      query: ({ page, limit, search, duty_status, shift_type, role }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        if (search) params.append("search", search);
        if (duty_status) params.append("duty_status", duty_status);
        if (shift_type) params.append("shift_type", shift_type);
        if (role) params.append("role", role);

        const queryString = params.toString();
        return queryString ? `?${queryString}` : "";
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const driverDetail = data?.data?.drivers ?? [];
          if (!Array.isArray(driverDetail) || driverDetail.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const driver of driverDetail) {
            if (driver.driver_details.duty_status)
              bucket.push(driver.driver_details.duty_status);
            if (driver.driver_details.license_number)
              bucket.push(driver.driver_details.license_number);
            if (driver.full_name) bucket.push(driver.full_name);
            if (driver.status) bucket.push(driver.status);
            if (driver.assigned_vehicle) bucket.push(driver.assigned_vehicle);
            if (driver.email) bucket.push(driver.email);
            if (driver.city) bucket.push(driver.city);
            if (driver.street) bucket.push(driver.street);
            if (driver.state) bucket.push(driver.state);
            if (driver.country) bucket.push(driver.country);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            driversSlice.util.updateQueryData("getDrivers", arg, (draft) => {
              const arr = draft?.data?.drivers ?? [];
              arr.forEach((driver) => {
                driver.driver_details.duty_status = pick(
                  driver.driver_details.duty_status,
                );
                driver.driver_details.license_number = pick(
                  driver.driver_details.license_number,
                );
                driver.full_name = pick(driver.full_name);
                driver.status = pick(driver.status);
                driver.assigned_vehicle = pick(driver.assigned_vehicle);
                driver.email = pick(driver.email);
                driver.city = pick(driver.city);
                driver.street = pick(driver.street);
                driver.state = pick(driver.state);
                driver.country = pick(driver.country);
              });
            }),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["drivers"],
    }),
    // GET Drivers List
    getDriverschedules: builder.query({
      query: ({ page, limit, id, search, status, shift_type }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (shift_type) params.append("shift_type", shift_type);

        const queryString = params.toString();
        return `schedules/${id}?${queryString}`;
      },

      // Dynamic Data Localization
      // async onQueryStarted(arg, { queryFulfilled, dispatch }) {
      //   try {
      //     const { data } = await queryFulfilled;

      //     const lang = norm(i18n.language || "en");
      //     if (lang === "en") return;

      //     const driverSchedule = data?.data?.schedules ?? [];
      //     if (!Array.isArray(driverSchedule) || driverSchedule.length === 0)
      //       return;

      //     // Collect strings to translate (skip PII like names/emails)
      //     const bucket = [];
      //     for (const driver of driverSchedule) {
      //       if (driver.shift_type) bucket.push(driver.shift_type);
      //       if (driver.recurrence_type) bucket.push(driver.recurrence_type);

      //       if (Array.isArray(driver.recurrence_days)) {
      //         driver.recurrence_days.forEach((day) => bucket.push(day));
      //       }
      //     }

      //     const map = await translateManyClient(bucket, lang, "en");
      //     const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

      //     dispatch(
      //       driversSlice.util.updateQueryData(
      //         "getDriverschedules",
      //         arg,
      //         (draft) => {
      //           const arr = draft?.data?.schedules ?? [];
      //           arr.forEach((driver) => {
      //             driver.shift_type = pick(driver.shift_type);
      //             driver.recurrence_type = pick(driver.recurrence_type);

      //             if (Array.isArray(driver.recurrence_days)) {
      //               driver.recurrence_days = driver.recurrence_days.map((day) =>
      //                 pick(day),
      //               );
      //             }
      //           });
      //         },
      //       ),
      //     );
      //   } catch {
      //     /* ignore translation errors */
      //   }
      // },

      providesTags: ["schedules"],
    }),
    getScheduleDetails: builder.query({
      query: (id) => `schedule/${id}`,
      providesTags: ["drivers"],
    }),

    getDriverDetails: builder.query({
      query: (id) => `${id}`,
      providesTags: ["drivers"],
    }),
    // Create New Driver
    createDriver: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["drivers"],
    }),
    // Update Driver
    updateDriver: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "Put",
        body: data,
      }),
      invalidatesTags: ["drivers"],
    }),
    // Create New Driver Schedule
    createDriverSchedule: builder.mutation({
      query: ({ data, id }) => ({
        url: `schedule/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["schedules"],
    }),
    // Update Driver Schedule
    updateDriverSchedule: builder.mutation({
      query: ({ data, sid }) => ({
        url: `schedule/${sid}`,
        method: "Put",
        body: data,
      }),
      invalidatesTags: ["schedules"],
    }),
    // Update Driver Status
    updateDriverStatus: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["drivers"],
    }),
    // UPDATE DRIVER SCHEDULE  STATUS API
    updateDriverScheduleStatus: builder.mutation({
      query: ({ data, id }) => ({
        url: `schedule/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["schedules"],
    }),
    // DELETE a Driver
    deleteDriver: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "Delete",
      }),
      invalidatesTags: ["drivers"],
    }),
    // DELETE Driver Schedule
    deleteDriverSchedule: builder.mutation({
      query: (scheduleId) => ({
        url: `schedule/${scheduleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["schedules"],
    }),
  }),
});

export const {
  useGetDriversQuery, // old use
  useGetDriverschedulesQuery, // new use
  useGetScheduleDetailsQuery,
  useGetDriverDetailsQuery, // not use
  useCreateDriverMutation,
  useCreateDriverScheduleMutation, // use new
  useUpdateDriverMutation,
  useUpdateDriverStatusMutation, // new use
  useUpdateDriverScheduleMutation, // new use
  useUpdateDriverScheduleStatusMutation,
  useDeleteDriverMutation,
  useDeleteDriverScheduleMutation,
} = driversSlice;
